import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { X } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '@core/utils/cn';

import { IconButton } from './IconButton';
import { useTranslation } from '@core/i18n';

/**
 * Side-anchored panel, glass, floating clear of the viewport edges.
 *
 * Glass is the right material here because a Sheet slides over content -- the map, or a
 * scrolling list -- rather than over a static background.
 *
 * For the mobile bottom sheet with drag and snap points, use Base UI's `Drawer`, not
 * this: Dialog has no gesture support, and faking snap points on top of it produces a
 * panel that ignores a flick and fights the scroll container.
 */
export interface SheetProps extends ComponentProps<typeof BaseDialog.Root> {}

export function Sheet(props: SheetProps) {
  return <BaseDialog.Root {...props} />;
}

Sheet.Trigger = BaseDialog.Trigger;
Sheet.Close = BaseDialog.Close;

export interface SheetContentProps {
  children: ReactNode;
  className?: string;
  side?: 'right' | 'left';
  /** Non-modal leaves the rest of the page interactive -- right for a map detail panel. */
  modal?: boolean;
}

Sheet.Content = function SheetContent({
  children,
  className,
  side = 'right',
  modal = true,
}: SheetContentProps) {
  return (
    <BaseDialog.Portal>
      {modal && (
        <BaseDialog.Backdrop
          className={cn(
            'fixed inset-0 z-40 bg-black/20',
            'ease-out-quart transition-opacity duration-300',
            'data-[ending-style]:opacity-0 data-[starting-style]:opacity-0',
            'motion-reduce:transition-none',
          )}
        />
      )}
      <BaseDialog.Viewport className="pointer-events-none fixed inset-0 z-50">
        <BaseDialog.Popup
          className={cn(
            'pointer-events-auto absolute inset-y-2 flex w-[min(420px,calc(100vw-1rem))] flex-col',
            'glass rounded-sheet text-fg outline-none',
            side === 'right' ? 'right-2' : 'left-2',
            'ease-spring transition-[transform,opacity] duration-[350ms]',
            'motion-reduce:transition-none',
            side === 'right'
              ? [
                  'data-[starting-style]:translate-x-[calc(100%+0.75rem)] data-[starting-style]:opacity-0',
                  'data-[ending-style]:translate-x-[calc(100%+0.75rem)] data-[ending-style]:opacity-0',
                ]
              : [
                  'data-[starting-style]:-translate-x-[calc(100%+0.75rem)] data-[starting-style]:opacity-0',
                  'data-[ending-style]:-translate-x-[calc(100%+0.75rem)] data-[ending-style]:opacity-0',
                ],
            className,
          )}
        >
          {children}
        </BaseDialog.Popup>
      </BaseDialog.Viewport>
    </BaseDialog.Portal>
  );
};

Sheet.Header = function SheetHeader({
  title,
  description,
}: {
  title: ReactNode;
  description?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-3 px-5 pt-5">
      <div className="min-w-0 flex-1">
        <BaseDialog.Title className="text-[19px] font-semibold tracking-[-0.017em]">
          {title}
        </BaseDialog.Title>
        {description && (
          <BaseDialog.Description className="text-fg-muted mt-1 text-[14px]">
            {description}
          </BaseDialog.Description>
        )}
      </div>
      <BaseDialog.Close
        render={
          <IconButton
            label={t.common.close}
            icon={<X />}
            variant="ghost"
            size="icon-sm"
            showTooltip={false}
          />
        }
      />
    </div>
  );
};

/** min-h-0 is what lets this actually scroll inside a flex column. */
Sheet.Body = function SheetBody({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4', className)}
      {...props}
    />
  );
};

Sheet.Footer = function SheetFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div className={cn('flex items-center justify-end gap-2 px-5 pb-5', className)} {...props} />
  );
};
